import { useState, useRef, useEffect, useCallback } from "react";
import { Character } from "@/mocks/characters";
import { supabase } from "@/lib/supabase";

interface Props {
  initial?: Character;
  onSubmit: (char: Character) => void;
  onCancel: () => void;
  mode: "add" | "edit";
}

interface ImageSlot {
  ref: string;
  preview: string;
  loading: boolean;
  isUploaded: boolean;
  isDragOver: boolean;
}

const defaultChar: Omit<Character, "id"> = {
  name: "",
  age: null,
  images: [""],
  link: "",
  description: "",
  tags: [],
  area: "",
  lat: 35.6762,
  lng: 139.6503,
  isActive: true,
  catchphrase: "",
  height: null,
  hobby: "",
  type: "",
};

const CharacterForm = ({ initial, onSubmit, onCancel, mode }: Props) => {
  const charId = useRef<string>(initial?.id ?? `new_${Date.now()}`);

  const [form, setForm] = useState<Omit<Character, "id">>(
    initial ? { ...initial } : { ...defaultChar }
  );
  const [ageInput, setAgeInput] = useState<string>(
    initial?.age != null ? String(initial.age) : ""
  );
  const [heightInput, setHeightInput] = useState<string>(
    initial?.height != null ? String(initial.height) : ""
  );
  const [slots, setSlots] = useState<ImageSlot[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const imgs = initial?.images ?? [""];
    const loaded: ImageSlot[] = imgs.map((img) => ({
      ref: img,
      preview: img,
      loading: false,
      isUploaded: img.includes("supabase") && img.includes("character-images"),
      isDragOver: false,
    }));
    setSlots(loaded.length > 0 ? loaded : [{ ref: "", preview: "", loading: false, isUploaded: false, isDragOver: false }]);
  }, []);

  const update = (key: keyof Omit<Character, "id">, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const setSlotLoading = (index: number, loading: boolean) => {
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, loading } : s)));
  };

  const setSlotDragOver = (index: number, isDragOver: boolean) => {
    setSlots((prev) => prev.map((s, i) => (i === index ? { ...s, isDragOver } : s)));
  };

  const handleFileUpload = async (index: number, file: File) => {
    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({ ...prev, images: "画像ファイルを選択してください" }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, images: "10MB以下の画像を選択してください" }));
      return;
    }
    setSlotLoading(index, true);
    setErrors((prev) => ({ ...prev, images: "" }));

    try {
      const previewUrl = URL.createObjectURL(file);

      const ext = file.name.split(".").pop() ?? "jpg";
      const fileName = `${charId.current}_${index}_${Date.now()}.${ext}`;
      const filePath = `characters/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("character-images")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("character-images")
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;

      setSlots((prev) =>
        prev.map((s, i) =>
          i === index
            ? { ref: publicUrl, preview: publicUrl, loading: false, isUploaded: true, isDragOver: false }
            : s
        )
      );
      URL.revokeObjectURL(previewUrl);
    } catch (err) {
      console.error("Upload error:", err);
      setErrors((prev) => ({ ...prev, images: "画像のアップロードに失敗しました。もう一度お試しください。" }));
      setSlotLoading(index, false);
    }
  };

  const handleUrlChange = (index: number, url: string) => {
    setSlots((prev) =>
      prev.map((s, i) =>
        i === index ? { ref: url, preview: url, loading: false, isUploaded: false, isDragOver: false } : s
      )
    );
  };

  // Drag & Drop handlers
  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSlotDragOver(index, true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSlotDragOver(index, false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSlotDragOver(index, false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(index, file);
  }, [slots]);

  const addSlot = () => {
    if (slots.length < 6) {
      setSlots((prev) => [...prev, { ref: "", preview: "", loading: false, isUploaded: false, isDragOver: false }]);
    }
  };

  const removeSlot = (index: number) => {
    if (slots.length <= 1) return;
    setSlots((prev) => prev.filter((_, i) => i !== index));
    fileInputRefs.current = fileInputRefs.current.filter((_, i) => i !== index);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) {
      update("tags", [...form.tags, t]);
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    update("tags", form.tags.filter((t) => t !== tag));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = "名前を入力してください";
    if (!form.link.trim()) errs.link = "リンクを入力してください";
    if (slots.every((s) => !s.ref.trim())) errs.images = "画像を1つ以上設定してください";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const ageVal = ageInput.trim() === "" ? null : parseInt(ageInput, 10);
    const heightVal = heightInput.trim() === "" ? null : parseInt(heightInput, 10);

    const char: Character = {
      ...form,
      age: ageVal,
      height: heightVal,
      id: initial?.id ?? charId.current,
      images: slots.filter((s) => s.ref.trim()).map((s) => s.ref),
    };
    onSubmit(char);
  };

  return (
    <div className="min-h-screen bg-pink-50" style={{ fontFamily: "'Nunito', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-pink-100 px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="w-9 h-9 flex items-center justify-center bg-pink-50 text-pink-500 rounded-full hover:bg-pink-100 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <span className="font-bold text-gray-800">
            {mode === "add" ? "キャラクターを追加" : "キャラクターを編集"}
          </span>
        </div>
        <img
          src="https://public.readdy.ai/ai/img_res/db459360-20e7-4f18-828f-b6f77daa6c55.png"
          alt="Girls' Map"
          className="h-8 w-auto object-contain"
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Basic Info */}
          <div className="bg-white rounded-2xl p-6 border border-pink-100">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-user-heart-line text-pink-400"></i>基本情報
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">名前 *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="例：さくら"
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
                />
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">
                  年齢
                  <span className="ml-2 text-pink-400 font-normal text-xs">空欄 → 「ひみつ」と表示</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={ageInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setAgeInput(val);
                  }}
                  placeholder="例：22（空欄でひみつ）"
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
                />
                {ageInput === "" && (
                  <p className="text-xs text-pink-400 mt-1 flex items-center gap-1">
                    <i className="ri-eye-off-line"></i>「ひみつ」として表示されます
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">タイプ</label>
                <input
                  type="text"
                  value={form.type}
                  onChange={(e) => update("type", e.target.value)}
                  placeholder="例：癒し系"
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">
                  身長 (cm)
                  <span className="ml-2 text-pink-400 font-normal text-xs">空欄 → 表示しない</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={heightInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setHeightInput(val);
                  }}
                  placeholder="例：158（空欄で非表示）"
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">趣味</label>
                <input
                  type="text"
                  value={form.hobby}
                  onChange={(e) => update("hobby", e.target.value)}
                  placeholder="例：カフェ巡り・映画"
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">エリア</label>
                <input
                  type="text"
                  value={form.area}
                  onChange={(e) => update("area", e.target.value)}
                  placeholder="例：渋谷・新宿"
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
                />
              </div>
            </div>
          </div>

          {/* Catchphrase & Description */}
          <div className="bg-white rounded-2xl p-6 border border-pink-100">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-chat-heart-line text-pink-400"></i>プロフィール文
            </h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">キャッチフレーズ</label>
                <input
                  type="text"
                  value={form.catchphrase}
                  onChange={(e) => update("catchphrase", e.target.value)}
                  placeholder="例：甘い時間を一緒に過ごしましょ♡"
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">自己紹介</label>
                <textarea
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  placeholder="自己紹介文を入力してください..."
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30 resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{form.description.length}/500</p>
              </div>
            </div>
          </div>

          {/* Images — Drag & Drop + Supabase Storage Upload */}
          <div className="bg-white rounded-2xl p-6 border border-pink-100">
            <h3 className="font-bold text-gray-800 mb-1 flex items-center gap-2">
              <i className="ri-image-line text-pink-400"></i>画像（複数登録可・ランダム表示）
            </h3>
            <p className="text-xs text-gray-400 mb-4">
              ドラッグ&ドロップ、またはクリックしてアップロード（最大6枚・各10MB以下）
            </p>
            {errors.images && <p className="text-red-400 text-xs mb-3">{errors.images}</p>}
            <div className="flex flex-col gap-4">
              {slots.map((slot, i) => (
                <div key={i} className="flex gap-3 items-start">
                  {/* Preview */}
                  <div className="w-20 h-20 rounded-xl overflow-hidden border-2 border-pink-200 flex-shrink-0 bg-pink-50 flex items-center justify-center">
                    {slot.loading ? (
                      <div className="flex flex-col items-center gap-1">
                        <i className="ri-loader-4-line text-pink-400 text-xl animate-spin"></i>
                        <span className="text-xs text-pink-400">保存中...</span>
                      </div>
                    ) : slot.preview ? (
                      <img src={slot.preview} alt="" className="w-full h-full object-cover object-top" />
                    ) : (
                      <i className="ri-image-add-line text-pink-300 text-2xl"></i>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col gap-2">
                    {/* Hidden file input */}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={(el) => { fileInputRefs.current[i] = el; }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(i, file);
                        e.target.value = "";
                      }}
                    />

                    {/* Drag & Drop zone */}
                    <div
                      onDragOver={(e) => handleDragOver(e, i)}
                      onDragLeave={(e) => handleDragLeave(e, i)}
                      onDrop={(e) => handleDrop(e, i)}
                      onClick={() => !slot.loading && fileInputRefs.current[i]?.click()}
                      className={`w-full flex flex-col items-center justify-center gap-1.5 px-3 py-4 border-2 border-dashed rounded-xl text-sm transition-all cursor-pointer ${
                        slot.isDragOver
                          ? "border-pink-500 bg-pink-100 text-pink-600 scale-[1.01]"
                          : slot.loading
                          ? "border-pink-200 bg-pink-50/50 text-pink-300 cursor-not-allowed"
                          : "border-pink-300 text-pink-500 hover:bg-pink-50 hover:border-pink-400"
                      }`}
                    >
                      {slot.isDragOver ? (
                        <>
                          <i className="ri-download-cloud-2-line text-xl"></i>
                          <span className="text-xs font-semibold">ここにドロップ！</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-cloud-line text-lg"></i>
                          <span className="text-xs">
                            {slot.isUploaded ? "クリックまたはドロップで変更" : "クリックまたはドロップでアップロード"}
                          </span>
                        </>
                      )}
                    </div>

                    {/* URL input — only show if not uploaded to Supabase */}
                    {!slot.isUploaded && (
                      <input
                        type="text"
                        value={slot.ref}
                        onChange={(e) => handleUrlChange(i, e.target.value)}
                        placeholder="またはURLを入力..."
                        className="w-full px-3 py-2 border border-pink-200 rounded-xl text-xs focus:outline-none focus:border-pink-400 bg-pink-50/30"
                      />
                    )}

                    {/* Status badge */}
                    {slot.ref && (
                      <div className="flex items-center gap-1">
                        {slot.isUploaded ? (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <i className="ri-cloud-fill"></i>クラウドに保存済み（全端末で表示）
                          </span>
                        ) : (
                          <span className="text-xs text-pink-500 flex items-center gap-1">
                            <i className="ri-link"></i>URL設定済み
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {slots.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSlot(i)}
                      className="w-9 h-9 flex items-center justify-center bg-red-50 text-red-400 rounded-full hover:bg-red-100 transition-all cursor-pointer flex-shrink-0 mt-0.5"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              ))}

              {slots.length < 6 && (
                <button
                  type="button"
                  onClick={addSlot}
                  className="flex items-center gap-2 text-sm text-pink-400 hover:text-pink-600 transition-colors cursor-pointer w-fit"
                >
                  <i className="ri-add-circle-line"></i>画像を追加
                </button>
              )}
            </div>
          </div>

          {/* Link */}
          <div className="bg-white rounded-2xl p-6 border border-pink-100">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-link text-pink-400"></i>リンク先
            </h3>
            <input
              type="url"
              value={form.link}
              onChange={(e) => update("link", e.target.value)}
              placeholder="https://example.com/..."
              className="w-full px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
            />
            {errors.link && <p className="text-red-400 text-xs mt-1">{errors.link}</p>}
          </div>

          {/* Tags */}
          <div className="bg-white rounded-2xl p-6 border border-pink-100">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-price-tag-3-line text-pink-400"></i>タグ
            </h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="タグを入力してEnter"
                className="flex-1 px-3 py-2.5 border border-pink-200 rounded-xl text-sm focus:outline-none focus:border-pink-400 bg-pink-50/30"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2.5 bg-pink-100 text-pink-500 rounded-xl text-sm font-medium hover:bg-pink-200 transition-all cursor-pointer whitespace-nowrap"
              >
                追加
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  className="flex items-center gap-1 bg-pink-50 text-pink-500 text-xs px-3 py-1 rounded-full border border-pink-200"
                >
                  #{tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="text-pink-300 hover:text-pink-500 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Display Toggle */}
          <div className="bg-white rounded-2xl p-6 border border-pink-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800">マップに表示</h3>
                <p className="text-xs text-gray-500 mt-0.5">オフにするとマップ・一覧に表示されません</p>
              </div>
              <button
                type="button"
                onClick={() => update("isActive", !form.isActive)}
                className={`relative w-12 h-6 rounded-full transition-all cursor-pointer ${
                  form.isActive ? "bg-pink-400" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    form.isActive ? "left-7" : "left-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-3 border-2 border-pink-200 text-pink-500 rounded-full font-semibold text-sm hover:bg-pink-50 transition-all cursor-pointer whitespace-nowrap"
            >
              キャンセル
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-gradient-to-r from-pink-400 to-rose-400 text-white rounded-full font-bold text-sm hover:from-pink-500 hover:to-rose-500 transition-all cursor-pointer whitespace-nowrap"
            >
              {mode === "add" ? "追加する" : "保存する"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CharacterForm;
